import {
  IconAlertOctagon,
  IconBook2,
  IconBuildingFactory2,
  IconBuildingWarehouse,
  IconCalendarStats,
  IconCash,
  IconClipboardList,
  IconFileInvoice,
  IconFileText,
  IconListCheck,
  IconPackage,
  IconRuler,
  IconSettings,
  IconShoppingCart,
  IconTool,
  IconTruckDelivery,
  IconUserCog,
  IconUsers,
} from '@tabler/icons-solidjs'
import type { JSX } from 'solid-js'
import { type Action, can, type Resource, type Role } from './permissions.ts'

export const BASE = '/prototype'

export interface ProtoNavItem {
  href: string
  label: string
  icon: () => JSX.Element
  resource: Resource
  action?: Action
  /** Roles that use this area daily. Others with read rights reach it from search and links. */
  primaryFor: readonly Role[]
  /** Screens drawn in this prototype; the rest open a short "not drawn yet" page. */
  drawn?: boolean
}

export interface ProtoNavGroup {
  label: string
  items: ProtoNavItem[]
}

const icon = (Component: (props: { strokeWidth?: number }) => JSX.Element) => () => (
  <Component strokeWidth={1.5} />
)

const SALES: readonly Role[] = ['admin', 'sales']
/** The general shop leads: the plan, all work centres, scrap and machines. */
const SHOP: readonly Role[] = ['admin', 'production_head', 'production']
const PRODUCTION: readonly Role[] = [...SHOP, 'cnc_head', 'engineer', 'chief_engineer', 'worker']
const WAREHOUSE: readonly Role[] = ['admin', 'storekeeper']
const FINANCE: readonly Role[] = ['admin', 'accountant']

export const NAV: ProtoNavGroup[] = [
  {
    label: 'Продажі',
    items: [
      {
        href: `${BASE}/orders`,
        label: 'Замовлення',
        icon: icon(IconClipboardList),
        resource: 'deals',
        // The head of production follows orders without selling.
        primaryFor: [...SALES, 'production_head'],
        drawn: true,
      },
      {
        href: `${BASE}/customers`,
        label: 'Клієнти',
        icon: icon(IconUsers),
        resource: 'deals',
        primaryFor: SALES,
        drawn: true,
      },
    ],
  },
  {
    label: 'Виробництво',
    items: [
      {
        href: `${BASE}/production`,
        label: 'Виробничі замовлення',
        icon: icon(IconBuildingFactory2),
        resource: 'production',
        primaryFor: [...SHOP, 'engineer', 'chief_engineer'],
        drawn: true,
      },
      {
        href: `${BASE}/production/plan`,
        label: 'План виробництва',
        icon: icon(IconCalendarStats),
        resource: 'production',
        primaryFor: [...SHOP, 'engineer', 'chief_engineer'],
        drawn: true,
      },
      {
        href: `${BASE}/production/queue`,
        label: 'Черга робіт',
        icon: icon(IconListCheck),
        resource: 'production',
        primaryFor: PRODUCTION,
        drawn: true,
      },
      {
        href: `${BASE}/production/scrap`,
        label: 'Брак',
        icon: icon(IconAlertOctagon),
        resource: 'production',
        primaryFor: [...SHOP, 'cnc_head', 'engineer', 'chief_engineer'],
        drawn: true,
      },
      {
        href: `${BASE}/engineering`,
        label: 'Конструкторський',
        icon: icon(IconRuler),
        resource: 'projects',
        primaryFor: ['admin', 'engineer', 'chief_engineer', 'production_head'],
        drawn: true,
      },
    ],
  },
  {
    label: 'Склад і закупівлі',
    items: [
      {
        href: `${BASE}/warehouse`,
        label: 'Залишки',
        icon: icon(IconBuildingWarehouse),
        resource: 'warehouse',
        primaryFor: [...WAREHOUSE, 'production_head'],
        drawn: true,
      },
      {
        href: `${BASE}/warehouse/purchasing`,
        label: 'Закупівлі',
        icon: icon(IconShoppingCart),
        resource: 'warehouse',
        primaryFor: [...WAREHOUSE, 'production_head'],
        drawn: true,
      },
      {
        href: `${BASE}/suppliers`,
        label: 'Постачальники',
        icon: icon(IconPackage),
        resource: 'suppliers',
        // Engineers pick materials and talk to suppliers about them (client, October).
        primaryFor: [...WAREHOUSE, 'accountant', 'engineer', 'chief_engineer'],
        drawn: true,
      },
    ],
  },
  {
    label: 'Бухгалтерія',
    items: [
      {
        href: `${BASE}/finance/invoices`,
        label: 'Рахунки',
        icon: icon(IconFileInvoice),
        resource: 'invoices',
        primaryFor: FINANCE,
        drawn: true,
      },
      {
        href: `${BASE}/finance/payment-requests`,
        label: 'Заявки на оплату',
        icon: icon(IconCash),
        resource: 'payment_requests',
        action: 'create',
        // The storekeeper buys materials and the shop orders repairs; both ask for payments.
        primaryFor: [...FINANCE, 'storekeeper', 'production', 'production_head'],
        drawn: true,
      },
    ],
  },
  {
    label: 'Логістика',
    items: [
      {
        href: `${BASE}/logistics`,
        label: 'Відправлення',
        icon: icon(IconTruckDelivery),
        resource: 'logistics',
        primaryFor: ['admin', 'logistician'],
        drawn: true,
      },
    ],
  },
  {
    label: 'Довідники',
    items: [
      {
        href: `${BASE}/documents`,
        label: 'Документи',
        icon: icon(IconFileText),
        resource: 'documents',
        primaryFor: [
          'admin',
          'sales',
          'engineer',
          'chief_engineer',
          'production_head',
          'cnc_head',
          'production',
          'storekeeper',
          'accountant',
          'logistician',
          'worker',
        ],
        drawn: true,
      },
      {
        href: `${BASE}/catalog`,
        label: 'Товари і техкарти',
        icon: icon(IconBook2),
        resource: 'projects',
        primaryFor: ['admin', 'engineer', 'chief_engineer'],
        drawn: true,
      },
      {
        href: `${BASE}/equipment`,
        label: 'Обладнання',
        icon: icon(IconTool),
        resource: 'equipment',
        primaryFor: [...SHOP, 'cnc_head'],
        drawn: true,
      },
    ],
  },
  {
    label: 'Адміністрування',
    items: [
      {
        href: `${BASE}/users`,
        label: 'Працівники',
        icon: icon(IconUserCog),
        resource: 'users',
        primaryFor: ['admin'],
        drawn: true,
      },
      {
        href: `${BASE}/settings`,
        label: 'Налаштування',
        icon: icon(IconSettings),
        resource: 'settings',
        primaryFor: ['admin'],
        drawn: true,
      },
    ],
  },
]

/** The sidebar shows only the areas a role works in, and only what RBAC lets it open. */
export const navFor = (role: Role): ProtoNavGroup[] =>
  NAV.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) => item.primaryFor.includes(role) && can(role, item.resource, item.action ?? 'read'),
    ),
  })).filter((group) => group.items.length > 0)

export const allNavItems = (): ProtoNavItem[] => NAV.flatMap((group) => group.items)

export const groupOf = (href: string): ProtoNavGroup | undefined =>
  NAV.find((group) => group.items.some((item) => href.startsWith(item.href)))
